import type { FC } from 'react';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
import idx from 'idx';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	IN_EDITOR_TEMPLATES_UPDATE_CONTENT_EXPERIENCE,
	IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE,
	ExperienceTimeout,
	ExperienceTrackerContext,
	stopExperienceOnError,
} from '@confluence/experience-tracker';
import { DialogsStateContainer } from '@confluence/dialogs';
import type { FlagsStateContainer } from '@confluence/flags';
import { withFlags } from '@confluence/flags';
import { Attribution, ErrorBoundary, isNotFoundError } from '@confluence/error-boundary';
import { getMonitoringClient } from '@confluence/monitoring';
import { useCreateContentFromTemplate } from '@confluence/create-content-from-template';
import { useSessionData } from '@confluence/session-data';
import type { Template } from '@confluence/template-list';
import { TemplateList } from '@confluence/template-list';
import {
	getTemplateKey,
	getTemplateIdAttributesForAnalytics,
	isBadThirdPartyBlueprintError,
	isBadEditedFirstPartyBlueprintError,
	getSearchQueryMatches,
	getErrorMessage,
} from '@confluence/template-utils';
import { CreateDialogContext } from '@confluence/create-dialog-context';
import { containsNoNetworkError } from '@confluence/network';
import { markErrorAsHandled } from '@confluence/graphql-error-processor';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState';
import { useIsLivePage } from '@confluence/live-pages-utils/entry-points/useIsLivePage';

import {
	collectCreateContentExperiences,
	collectUpdateContentExperiences,
} from './experienceTracking';
import { OverwriteWarningModal } from './OverwriteWarningModal';
import { AlwaysDiscardChangesModal } from './AlwaysDiscardChangesModal';
import { SearchBar } from './SearchBar';
import { TemplatePanelSpaceSelector } from './TemplatePanelSpaceSelector';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PanelWrapper = styled.div({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InputFieldsWrapper = styled.div({
	padding: `0 ${token('space.050', '4px')}`,
});

const templateListWrapperStyles = xcss({
	marginTop: 'space.200',
});

type TemplatePanelProps = {
	parentPageId: string;
	currentSpaceKey: string;
	contentId: string;
	isPanelDisabled?: boolean;
	onContentUpdated?: () => void;
	beforeTemplateSelect?: () => void;
	beforeUpdateRequest?: () => void;
	onError?: () => void;
	hasUserAddedContent: boolean | null;
	title: string;
	templateAllowList?: string[];
	isAlwaysDiscardChanges?: boolean;
	isSpacePickerDisabled?: boolean;
};

export enum ErrorCodes {
	VALIDATION_ERROR = 400,
	AUTHORIZATION_ERROR = 401,
	NOT_FOUND_ERROR = 404,
	CONTENT_ERROR = 500,
}

type InjectedFlagsProps = {
	flags: FlagsStateContainer;
};

type InjectedTemplatePanelProps = InjectedFlagsProps & TemplatePanelProps;

export const TemplatePanel: FC<TemplatePanelProps> = withFlags(
	({
		isPanelDisabled = false,
		parentPageId,
		currentSpaceKey,
		contentId,
		onContentUpdated,
		beforeUpdateRequest,
		beforeTemplateSelect,
		hasUserAddedContent,
		title,
		flags,
		onError,
		templateAllowList,
		isAlwaysDiscardChanges,
		isSpacePickerDisabled,
	}: InjectedTemplatePanelProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const experienceTracker = useContext(ExperienceTrackerContext);
		const intl = useIntl();
		const { userId: currentUserId } = useSessionData();
		const routeDataRef = useRouteDataRef();
		const isLivePage = useIsLivePage();

		const { showBlueprintVariableWizard, showTemplateVariableInputDrawer } =
			useContext(CreateDialogContext);

		const createFromTemplate = useCreateContentFromTemplate();
		const [selectedSpaceKey, setSelectedSpaceKey] = useState(currentSpaceKey);
		const [searchQuery, setSearchQuery] = useState('');
		const [selectedTemplateKey, setSelectedTemplateKey] = useState('');
		const [isDisabled, setIsDisabled] = useState(isPanelDisabled);
		const [isCreating, setIsCreating] = useState(false);

		// These variables needed for analytic events.
		// No need to put them into `state`, since they are not
		// rendered
		const hasSpaceChanged = useRef(false);

		useEffect(() => {
			if (
				showTemplateVariableInputDrawer ||
				showBlueprintVariableWizard ||
				isPanelDisabled ||
				isCreating
			) {
				setIsDisabled(true);
			} else {
				setIsDisabled(false);
				// @Todo CTE-337 reset value of stored template until dialog close callback is made
				setSelectedTemplateKey('');
			}
		}, [isCreating, showTemplateVariableInputDrawer, showBlueprintVariableWizard, isPanelDisabled]);

		const handleError = (e) => {
			onError && onError();
			const errorCode = idx(e, (_) => _.graphQLErrors[0].originalError.response.status);

			const isNotFoundGraphqlError = isNotFoundError(e);
			const isThirdPartyBlueprintError = isBadThirdPartyBlueprintError(e);
			const isEditedFirstPartyBlueprintError = isBadEditedFirstPartyBlueprintError(e);
			const isNoNetworkError = containsNoNetworkError(e);

			void flags.showErrorFlag(
				getErrorMessage(errorCode, isThirdPartyBlueprintError, isEditedFirstPartyBlueprintError),
			);

			if (
				isThirdPartyBlueprintError ||
				isEditedFirstPartyBlueprintError ||
				isNotFoundGraphqlError ||
				isNoNetworkError
			) {
				experienceTracker.succeed({
					name: IN_EDITOR_TEMPLATES_UPDATE_CONTENT_EXPERIENCE,
				});
				experienceTracker.abort({
					name: IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE,
					reason: isThirdPartyBlueprintError
						? 'third party blueprint error'
						: isNotFoundGraphqlError
							? 'not found graphql error'
							: isNoNetworkError
								? 'no network error'
								: 'edited first party blueprint error',
				});
				if (isNoNetworkError) {
					markErrorAsHandled(e);
				}
			} else {
				getMonitoringClient().submitError(e, {
					attribution: Attribution.TAILORED_EXPERIENCES,
				});

				stopExperienceOnError(IN_EDITOR_TEMPLATES_UPDATE_CONTENT_EXPERIENCE, e);
				stopExperienceOnError(IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE, e);
			}

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'templateDrawer',
					action: 'error',
					actionSubject: 'editPageScreen',
					actionSubjectId: 'contentCreateWithTemplateError',
					attributes: {
						errorMessage: e.message,
					},
				},
			}).fire();
		};

		const handleSpaceChange = useCallback(
			({ spaceKey: newSpaceKey }: any): void => {
				setSelectedSpaceKey(newSpaceKey);
			},
			[setSelectedSpaceKey],
		);

		const handleSearchChange = useCallback(
			(searchQuery: string): void => {
				setSearchQuery(searchQuery);
			},
			[setSearchQuery],
		);

		const handleSearchFocus = () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'templateDrawer',
					action: 'clicked',
					actionSubject: 'textField',
					actionSubjectId: 'templateSearchBar',
				},
			}).fire();
		};

		const closeDraftDiscardModal = () => {
			// todo fire analytics event https://hello.atlassian.net/browse/CTE-677
		};

		const handleTemplateSelectWithModal =
			(dialogs: DialogsStateContainer) =>
			(template: Template, additionalStatAttributes?: object): void => {
				setSelectedTemplateKey(getTemplateKey(template));
				beforeTemplateSelect && beforeTemplateSelect();

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						source: 'templateDrawer',
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'templateOption',
						attributes: {
							currentRouteName: routeDataRef?.current?.routeName,
							currentRouteIsLive: isLivePage,
							searchQueryPresent: !!searchQuery.length,
							spaceChanged: selectedSpaceKey !== currentSpaceKey,
							...getTemplateIdAttributesForAnalytics(template),
							...additionalStatAttributes,
							searchQueryMatches: getSearchQueryMatches(template, intl, currentUserId, searchQuery),
						},
					},
				}).fire();
				hasSpaceChanged.current = false;

				if (hasUserAddedContent) {
					const modal = isAlwaysDiscardChanges ? AlwaysDiscardChangesModal : OverwriteWarningModal;

					dialogs.showDialog(modal, {
						onCreate: handleTemplateSelect,
						onClose: closeDraftDiscardModal,
						template,
					});
				} else {
					handleTemplateSelect(template);
				}
			};

		const handleTemplateSelect = (template: Template, replaceContent: boolean = true): void => {
			setIsCreating(true);
			const shouldInjectContent = replaceContent && contentId;

			experienceTracker.start({
				name: IN_EDITOR_TEMPLATES_UPDATE_CONTENT_EXPERIENCE,
				collect: shouldInjectContent
					? collectUpdateContentExperiences
					: collectCreateContentExperiences,
			});
			experienceTracker.start({
				name: IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE,
				timeout: ExperienceTimeout.IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR,
			});

			const options = {
				...(onContentUpdated && { onContentUpdated }),
				onVariableInputError: handleError,
				beforeUpdateRequest,
				templateOriginSpaceKey: selectedSpaceKey,
				parentPageId,
				...(shouldInjectContent && { contentId }),
				...(shouldInjectContent && title && { title }),
			};

			createFromTemplate(template, currentSpaceKey, options)
				.then(() => {
					setIsCreating(false);
				})
				.catch((e) => {
					setIsCreating(false);
					handleError(e);
				});
		};

		return (
			<ErrorBoundary attribution={Attribution.TAILORED_EXPERIENCES}>
				<Subscribe to={[DialogsStateContainer]}>
					{(dialogs: DialogsStateContainer) => (
						<PanelWrapper data-testid="template-panel">
							<InputFieldsWrapper>
								{!isSpacePickerDisabled && (
									<TemplatePanelSpaceSelector
										onChange={handleSpaceChange}
										spaceKey={currentSpaceKey}
										isDisabled={isDisabled}
									/>
								)}
								<SearchBar
									onChange={handleSearchChange}
									onFocus={handleSearchFocus}
									isDisabled={isDisabled}
								/>
							</InputFieldsWrapper>
							<Box xcss={templateListWrapperStyles}>
								<TemplateList
									onTemplateSelect={handleTemplateSelectWithModal(dialogs)}
									spaceKey={selectedSpaceKey}
									templateBlueprintAllowList={templateAllowList}
									currentSpaceKey={currentSpaceKey}
									searchQuery={searchQuery}
									selectedTemplateKey={selectedTemplateKey}
									isDisabled={isDisabled}
								/>
							</Box>
						</PanelWrapper>
					)}
				</Subscribe>
			</ErrorBoundary>
		);
	},
) as any;
